import { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { User, Mail, Calendar, MessageSquare, Heart, Edit2, Check, X } from 'lucide-react'
import { format } from 'date-fns'
import toast from 'react-hot-toast'

const Profile = () => {
  const { user, updateProfile } = useAuth()
  const [isEditing, setIsEditing] = useState(false)
  const [displayName, setDisplayName] = useState(user?.displayName || '')
  const [isSaving, setIsSaving] = useState(false)

  const handleSave = async () => {
    if (!displayName.trim() || displayName === user.displayName) {
      setIsEditing(false)
      return
    }

    setIsSaving(true)
    const result = await updateProfile({ displayName: displayName.trim() })
    
    if (result.success) {
      setIsEditing(false)
    }
    setIsSaving(false)
  }

  const handleCancel = () => {
    setDisplayName(user?.displayName || '')
    setIsEditing(false)
  }

  if (!user) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Please sign in to view your profile</p>
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Your Profile</h1>
      
      <div className="card">
        {/* Profile header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center space-x-4">
            <div className="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center">
              <User className="w-10 h-10 text-primary-600" />
            </div>
            <div className="flex-1">
              {isEditing ? (
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    className="input-field max-w-xs"
                    maxLength={50}
                  />
                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="p-2 text-green-600 hover:bg-green-50 rounded-full"
                  >
                    <Check className="w-5 h-5" />
                  </button>
                  <button
                    onClick={handleCancel}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-full"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <h2 className="text-xl font-bold text-gray-900">{user.displayName}</h2>
                  <button
                    onClick={() => setIsEditing(true)}
                    className="p-1 text-gray-400 hover:text-gray-600"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                </div>
              )}
              <p className="text-gray-500 flex items-center mt-1">
                <Mail className="w-4 h-4 mr-1" />
                {user.email}
              </p>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 divide-x divide-gray-200">
          <div className="p-6 text-center">
            <div className="flex items-center justify-center space-x-2 text-primary-600 mb-1">
              <MessageSquare className="w-5 h-5" />
              <span className="text-2xl font-bold">{user.postCount || 0}</span>
            </div>
            <p className="text-sm text-gray-500">Confessions</p>
          </div>
          <div className="p-6 text-center">
            <div className="flex items-center justify-center space-x-2 text-anonymous-600 mb-1">
              <Heart className="w-5 h-5" />
              <span className="text-2xl font-bold">{user.commentCount || 0}</span>
            </div>
            <p className="text-sm text-gray-500">Comments</p>
          </div>
        </div>

        {/* Account info */}
        <div className="p-6 border-t border-gray-200">
          <h3 className="text-sm font-medium text-gray-900 mb-4">Account Information</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Member since</span>
              <span className="text-sm text-gray-900">
                {format(new Date(user.createdAt), 'MMMM d, yyyy')}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Account type</span>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800 capitalize">
                {user.role}
              </span>
            </div>
          </div>
        </div>

        {/* Privacy notice */}
        <div className="p-6 bg-green-50 border-t border-green-100">
          <h3 className="text-sm font-medium text-green-900 mb-2">Privacy Settings</h3>
          <p className="text-sm text-green-700">
            Your identity is protected. You choose whether to post anonymously or with your 
            display name "{user.displayName}" on every confession and comment.
          </p>
        </div>
      </div>
    </div>
  )
}

export default Profile